import { type MutateOptions, useMutation } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { gqlClient } from '@/lib';
import type { QueryError } from '@/utils/errors';
import { useLibraryCache } from '../useLibraryCache';
import type {
  LeaveScheduleMutation,
  LeaveScheduleMutationVariables,
} from './useLeaveSchedule.generated';

const query = gql`
  mutation LeaveSchedule($scheduleId: ID!) {
    leaveSchedule(scheduleId: $scheduleId)
  }
`;

export const useLeaveSchedule = () => {
  const { removeLibraryItem } = useLibraryCache();

  const { mutate, ...rest } = useMutation<
    LeaveScheduleMutation,
    QueryError,
    LeaveScheduleMutationVariables
  >({
    mutationFn: (variables) =>
      gqlClient.request<LeaveScheduleMutation, LeaveScheduleMutationVariables>(
        query,
        variables
      ),
    onSuccess: (data, variables) => {
      removeLibraryItem(variables.scheduleId);
    },
  });

  const leaveSchedule = (
    scheduleId: string,
    options: MutateOptions<
      LeaveScheduleMutation,
      QueryError,
      LeaveScheduleMutationVariables
    >
  ) => {
    return mutate(
      { scheduleId },
      {
        ...options,
        onSuccess: (...args) => {
          // Timeout ensures state is updated before navigating
          setTimeout(() => options.onSuccess?.(...args));
        },
      }
    );
  };

  return {
    ...rest,
    leaveSchedule,
  };
};
